/**
 * `CallBanner` — the 52px active-call strip shown in the Telefon module while a
 * call is live (`inCall`). Pulsing phone icon, the dialed number with an inline
 * live-timer chip, contact subtext, an animated 4-bar waveform, two glassy
 * quick-action chips, a divider, four glassy icon buttons, and the red
 * "Auflegen" button. Animations live in `hero.css` under `.mkhero`
 * (callPulse / callBlink / callWave). 1:1 with the design (prototype lines
 * 286–318).
 */
import { ld } from "@/lib/theme/light-dark";
import {
  ArrowLeftRight,
  FilePlus,
  Grid3x3,
  ListChecks,
  MicOff,
  Pause,
  PhoneCall,
  PhoneOff,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../hero-tokens";

/** Per-bar animation delays for the 4-bar waveform (design 296–301). */
const WAVE_DELAYS = ["0s", "0.18s", "0.36s", "0.54s"] as const;

function Waveform(): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 2.5,
        height: 18,
        marginLeft: 3,
        flexShrink: 0,
      }}
    >
      {WAVE_DELAYS.map((delay) => (
        <span
          key={delay}
          className="mkhero-callwave"
          style={{
            width: 3,
            height: "100%",
            borderRadius: 2,
            background: H.accent,
            opacity: 0.7,
            transformOrigin: "bottom",
            animationDelay: delay,
          }}
        />
      ))}
    </div>
  );
}

/** Glassy white-on-banner styling shared by the action chips + icon buttons. */
const GLASS_BG = ld("rgba(255,255,255,0.75)", "rgba(26,28,31,0.75)");
const GLASS_BORDER = `1px solid ${ld("rgba(11,15,25,0.09)", "rgba(250,250,250,0.09)")}`;

function IconBtn(props: {
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <span
      title={props.title}
      style={{
        width: 30,
        height: 30,
        borderRadius: 8,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: GLASS_BG,
        border: GLASS_BORDER,
        color: ld("rgba(11,15,25,0.62)", "rgba(250,250,250,0.62)"),
        flexShrink: 0,
        cursor: "pointer",
      }}
    >
      {props.children}
    </span>
  );
}

function ActionChip(props: {
  readonly icon: ReactNode;
  readonly label: string;
}): ReactNode {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 30,
        padding: "0 11px",
        borderRadius: 8,
        background: GLASS_BG,
        border: GLASS_BORDER,
        color: ld("rgba(11,15,25,0.6)", "rgba(250,250,250,0.6)"),
        fontFamily: H.sans,
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {props.icon}
      {props.label}
    </span>
  );
}

export function CallBanner(props: {
  readonly onHangup?: () => void;
}): ReactNode {
  const t = useTranslations("marketing.callBanner");
  return (
    <div
      style={{
        height: 52,
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: "0 14px",
        background: ld("#E2F0EC", "#293532"),
        borderBottom: "1px solid rgba(0,128,112,0.18)",
        overflow: "hidden",
      }}
    >
      {/* Pulsing phone icon */}
      <span
        style={{
          position: "relative",
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: H.accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: ld("#fff", "#181b1f"),
          flexShrink: 0,
        }}
      >
        <span
          className="mkhero-callpulse"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background: H.accent,
          }}
        />
        <PhoneCall width={15} height={15} style={{ position: "relative" }} />
      </span>

      {/* Number (with inline timer chip) + subtext */}
      <div
        style={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span
            style={{
              fontFamily: H.sans,
              fontSize: 14,
              fontWeight: 700,
              color: H.ink,
              letterSpacing: "-0.01em",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {t("k492419705126")}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              height: 19,
              padding: "0 7px",
              borderRadius: 6,
              background: "rgba(0,128,112,0.12)",
              fontFamily: H.label,
              fontSize: 11,
              fontWeight: 600,
              color: H.accent,
            }}
          >
            <span
              className="mkhero-callblink"
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: H.accent,
              }}
            />
            {t("k0042")}
          </span>
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginTop: 1,
          }}
        >
          {t.rich("kontaktStatusTrail", {
            b: (chunks) => (
              <span style={{ color: H.accent, fontWeight: 600 }}>{chunks}</span>
            ),
          })}
        </div>
      </div>

      <Waveform />

      {/* Right cluster — actions + divider + icons + hang up */}
      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 7,
          flexShrink: 0,
        }}
      >
        <ActionChip
          icon={<ListChecks width={14} height={14} strokeWidth={1.8} />}
          label={t("aufgabe")}
        />
        <ActionChip
          icon={<FilePlus width={14} height={14} strokeWidth={1.8} />}
          label={t("notiz")}
        />
        <span
          style={{
            width: 1,
            height: 22,
            background: ld("rgba(11,15,25,0.1)", "rgba(250,250,250,0.1)"),
            margin: "0 1px",
          }}
        />
        <IconBtn title={t("tastenfeld")}>
          <Grid3x3 width={15} height={15} strokeWidth={1.9} />
        </IconBtn>
        <IconBtn title={t("weiterleiten")}>
          <ArrowLeftRight width={15} height={15} strokeWidth={1.9} />
        </IconBtn>
        <IconBtn title={t("halten")}>
          <Pause width={15} height={15} strokeWidth={1.9} />
        </IconBtn>
        <IconBtn title={t("stummschalten")}>
          <MicOff width={15} height={15} strokeWidth={1.9} />
        </IconBtn>
        <button
          type="button"
          onClick={props.onHangup}
          title={t("auflegen")}
          style={
            {
              appearance: "none",
              border: "none",
              cursor: "pointer",
              height: 30,
              padding: "0 13px",
              borderRadius: 8,
              background: "#D8453C",
              color: "#fff",
              fontFamily: H.sans,
              fontSize: 12.5,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              boxShadow: `0 3px 8px -2px ${ld("rgba(216,69,60,0.55)", "rgba(0,0,0,0.85)")}`,
            } satisfies CSSProperties
          }
        >
          <PhoneOff width={15} height={15} strokeWidth={2} />
          {t("auflegen")}
        </button>
      </div>
    </div>
  );
}
